import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import {
  ADMIN_PANEL_TABS,
  ADMIN_PANEL_TAB_SLUG,
  DEFAULT_ADMIN_PANEL_SLUG,
  adminTabSlugToValue,
  adminTabValueToSlug,
} from '../constants';

interface IRouteParams {
  tabSlug?: string;
}

/**
 * Route-synced tab + search state for the Admin dashboard.
 *
 * - Active tab lives in the URL path (/home/:tabSlug) so refresh,
 *   back/forward and shared links work.
 * - Resolves activeSlug from useParams, matchPath, or location.pathname
 *   to ensure tab highlights accurately when nested inside /home.
 * - Search is local input state, reset on tab change.
 */
export const useAdminDashboardTabs = () => {
  const { path, url } = useRouteMatch();
  const { tabSlug } = useParams<IRouteParams>();
  const location = useLocation();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const activeSlug: ADMIN_PANEL_TAB_SLUG = useMemo(() => {
    // 1. Direct param if provided by router
    let rawSlug = tabSlug;

    // 2. Match sub-path relative to current matched route (e.g. /home/:tabSlug)
    if (!rawSlug) {
      const match = matchPath<IRouteParams>(location.pathname, {
        path: `${path}/:tabSlug`,
      });
      rawSlug = match?.params?.tabSlug;
    }

    // 3. Fallback: Parse segment directly from location.pathname relative to baseUrl/path
    if (!rawSlug) {
      const basePath = (url || path || '/home').replace(/\/+$/, '');
      if (location.pathname.startsWith(basePath)) {
        const after = location.pathname.slice(basePath.length).replace(/^\/+/, '');
        const segment = after.split('/')[0];
        if (segment) {
          rawSlug = segment;
        }
      }
    }

    if (rawSlug && (Object.values(ADMIN_PANEL_TAB_SLUG) as string[]).includes(rawSlug)) {
      return rawSlug as ADMIN_PANEL_TAB_SLUG;
    }
    return DEFAULT_ADMIN_PANEL_SLUG;
  }, [tabSlug, location.pathname, path, url]);

  const activeTab = useMemo(() => adminTabSlugToValue(activeSlug), [activeSlug]);

  const activeTabDef = useMemo(
    () => ADMIN_PANEL_TABS.find((tab) => tab.slug === activeSlug) ?? ADMIN_PANEL_TABS[0],
    [activeSlug],
  );

  // Reset search when switching tabs
  useEffect(() => {
    setSearchTerm('');
  }, [activeSlug]);

  const handleTabChange = useCallback(
    (_event: unknown, slug: string) => {
      setSearchTerm('');
      const cleanUrl = url.replace(/\/+$/, '');
      const target = `${cleanUrl}/${slug}${location.search ?? ''}`;
      if (history.location.pathname !== `${cleanUrl}/${slug}`) {
        history.push(target);
      }
    },
    [history, url, location.search],
  );

  const navigateToSlug = useCallback(
    (value: string) => {
      const slug = adminTabValueToSlug(value);
      setSearchTerm('');
      const cleanUrl = url.replace(/\/+$/, '');
      history.push(`${cleanUrl}/${slug}`);
    },
    [history, url],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  return {
    path,
    url,
    tabs: ADMIN_PANEL_TABS,
    activeSlug,
    activeTab,
    activeTabDef,
    searchTerm,
    setSearchTerm,
    handleTabChange,
    navigateToSlug,
    handleClearSearch,
  };
};
